import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowClockwise, CashStack, ChatLeftText, ExclamationTriangleFill } from 'react-bootstrap-icons';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';

import {
    listSupportTickets,
    replyToSupportTicket,
    requestSupportTicketRefund,
    updateSupportTicketStatus,
    type SupportTicket,
} from '../api/support_tickets';
import './SuperAdminFeedback-new.css';
import './SuperAdminSupportTickets-new.css';

type TicketFilter = 'all' | 'open' | 'under_review' | 'refunds' | 'closed';

const FILTERS: Array<{ label: string; value: TicketFilter }> = [
    { label: 'All', value: 'all' },
    { label: 'Open', value: 'open' },
    { label: 'In review', value: 'under_review' },
    { label: 'Refunds', value: 'refunds' },
    { label: 'Closed', value: 'closed' },
];

function formatDate(value: string): string
{
    return new Date(value).toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
}

function formatStatus(status: string): string
{
    return status.replace(/_/g, ' ');
}

function statusClass(status: string): string
{
    if (status === 'resolved' || status === 'refund_completed') return 'sst-status sst-status-success';
    if (status === 'rejected' || status === 'refund_failed') return 'sst-status sst-status-danger';
    if (status === 'refund_requested' || status === 'refund_processing') return 'sst-status sst-status-warning';
    return 'sst-status sst-status-info';
}

function paymentLabel(ticket: SupportTicket): string
{
    return (ticket.payment?.payment_type || 'Billing payment').replace(/_/g, ' ');
}

function remainingRefundableCents(ticket: SupportTicket): number
{
    return Math.max(0, (ticket.payment?.amount_cents || 0) - (ticket.payment?.refunded_amount_cents || 0));
}

function formatUsd(cents: number): string
{
    return `$${(Math.max(0, cents) / 100).toFixed(2)}`;
}

function isLlmUsageRecharge(ticket: SupportTicket): boolean
{
    return ticket.payment?.payment_type === 'llm_usage_recharge';
}

function isActionableTicket(ticket: SupportTicket): boolean
{
    return ['open', 'under_review'].includes(ticket.status);
}

export default function SuperAdminSupportTickets(): React.ReactElement
{
    const [items, setItems] = useState<SupportTicket[]>([]);
    const [selected, setSelected] = useState<SupportTicket | null>(null);
    const [filter, setFilter] = useState<TicketFilter>('all');
    const [reply, setReply] = useState('');
    const [refundAmount, setRefundAmount] = useState('');
    const [refundConfirmationAmountCents, setRefundConfirmationAmountCents] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState<'reply' | 'refund' | 'reject' | null>(null);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () =>
    {
        setLoading(true);
        setError(null);
        try
        {
            const data = await listSupportTickets(filter === 'all' ? undefined : filter);
            setItems(data.items);
            setSelected((current) => data.items.find((item) => item.id === current?.id) || null);
        }
        catch (err: any)
        {
            setError(err?.response?.data?.detail || 'Unable to load support tickets.');
        }
        finally
        {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => { void load(); }, [load]);

    const openCount = useMemo(
        () => items.filter((ticket) => ['open', 'under_review', 'refund_requested', 'refund_processing'].includes(ticket.status)).length,
        [items],
    );

    async function runTicketAction(action: 'reply' | 'refund' | 'reject', callback: () => Promise<SupportTicket>): Promise<void>
    {
        setSubmitting(action);
        setError(null);
        try
        {
            const updated = await callback();
            setSelected(updated);
            if (action === 'reply') setReply('');
            if (action === 'refund')
            {
                setRefundAmount('');
                setRefundConfirmationAmountCents(null);
            }
            await load();
        }
        catch (err: any)
        {
            setError(err?.response?.data?.detail || 'The ticket could not be updated. Please try again.');
        }
        finally
        {
            setSubmitting(null);
        }
    }

    function handleReply(): void
    {
        if (!selected || !isActionableTicket(selected) || !reply.trim()) return;
        void runTicketAction('reply', () => replyToSupportTicket(selected.id, reply.trim()));
    }

    function handleRefund(): void
    {
        if (!selected || !isActionableTicket(selected)) return;
        if (remainingRefundableCents(selected) <= 0)
        {
            setError('This charge has already been fully refunded.');
            return;
        }
        const amount = refundAmount.trim() ? Math.round(Number(refundAmount) * 100) : remainingRefundableCents(selected);
        if (refundAmount.trim() && (!amount || amount < 1))
        {
            setError('Enter a valid refund amount.');
            return;
        }
        if (amount > remainingRefundableCents(selected))
        {
            setError('Refund amount exceeds the remaining charged amount.');
            return;
        }
        setRefundConfirmationAmountCents(amount);
    }

    function confirmRefund(): void
    {
        if (!selected || refundConfirmationAmountCents === null) return;
        void runTicketAction('refund', () => requestSupportTicketRefund(selected.id, refundConfirmationAmountCents));
    }

    function handleReject(): void
    {
        if (!selected || !isActionableTicket(selected)) return;
        void runTicketAction('reject', () => updateSupportTicketStatus(selected.id, 'rejected'));
    }

    return (
        <div className="saf-page sst-page">
            <Modal
                show={refundConfirmationAmountCents !== null}
                onHide={() => submitting !== 'refund' && setRefundConfirmationAmountCents(null)}
                dialogClassName="sst-refund-confirm-modal"
                centered
                backdrop={submitting === 'refund' ? 'static' : true}
                keyboard={submitting !== 'refund'}
            >
                <Modal.Header closeButton={submitting !== 'refund'} className="sst-refund-confirm-header">
                    <div className="sst-refund-confirm-heading">
                        <span className="sst-refund-confirm-icon" aria-hidden="true"><CashStack size={18} /></span>
                        <div>
                            <Modal.Title>Confirm refund</Modal.Title>
                            <p>Review this refund before it is submitted to Stripe.</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="sst-refund-confirm-body">
                    <p className="sst-refund-confirm-copy">
                        Refund <strong>{formatUsd(refundConfirmationAmountCents || 0)}</strong> to the customer&apos;s original payment method?
                    </p>
                    {selected && isLlmUsageRecharge(selected) && refundConfirmationAmountCents !== null && (
                        (() =>
                        {
                            const creditBalance = selected.llm_usage_credit_balance_before_debt_cents ?? 0;
                            const existingDebt = selected.llm_usage_credit_pending_debt_cents ?? 0;
                            const overageDebt = Math.max(0, refundConfirmationAmountCents - creditBalance);
                            return overageDebt > 0 ? (
                                <div className="sst-refund-confirm-warning">
                                    <strong>{formatUsd(overageDebt)} will be added as overage debt.</strong>
                                    <span>
                                        The organization has {formatUsd(creditBalance)} in remaining LLM Usage Credits. This debt will be deducted from a future LLM Usage Credits recharge.
                                        {existingDebt > 0 ? ` Its pending debt will become ${formatUsd(existingDebt + overageDebt)}.` : ''}
                                    </span>
                                </div>
                            ) : null;
                        })()
                    )}
                    <p className="sst-refund-confirm-note">This action cannot be undone from AcceliQA after Stripe processes the refund.</p>
                </Modal.Body>
                <Modal.Footer className="sst-refund-confirm-footer">
                    <button type="button" className="sst-button sst-button-secondary" onClick={() => setRefundConfirmationAmountCents(null)} disabled={submitting === 'refund'}>Cancel</button>
                    <button type="button" className="sst-button sst-button-danger" onClick={confirmRefund} disabled={submitting === 'refund'}>
                        {submitting === 'refund' ? <><Spinner animation="border" size="sm" /> Processing...</> : <><CashStack size={15} /> Confirm refund</>}
                    </button>
                </Modal.Footer>
            </Modal>
            <header className="saf-page-header">
                <div>
                    <h1 className="saf-page-title">Billing Support Tickets</h1>
                    <p className="saf-page-subtitle">Review charge concerns, communicate with organization admins, and manage eligible refunds.</p>
                </div>
                <button type="button" className="sst-refresh-button" onClick={() => void load()} disabled={loading}>
                    {loading ? <Spinner animation="border" size="sm" /> : <ArrowClockwise size={15} />}
                    Refresh
                </button>
            </header>

            <div className="sst-filter-bar" aria-label="Filter support tickets">
                <div className="saf-filter-switcher">
                    {FILTERS.map((item) => (
                        <button key={item.value} type="button" className={`saf-filter-pill ${filter === item.value ? 'saf-filter-pill-active' : ''}`} onClick={() => setFilter(item.value)}>
                            {item.label}
                        </button>
                    ))}
                </div>
                <span className="sst-count">{loading ? 'Loading tickets...' : `${openCount} requiring attention`}</span>
            </div>

            {error && <div className="sst-alert"><ExclamationTriangleFill size={16} /> {error}</div>}

            <div className="sst-layout">
                <section className="sst-panel sst-queue" aria-label="Support ticket queue">
                    <div className="sst-panel-header">
                        <div>
                            <h2>Ticket queue</h2>
                            <p>{loading ? 'Loading...' : `${items.length} ticket${items.length === 1 ? '' : 's'}`}</p>
                        </div>
                    </div>

                    {loading ? (
                        <div className="sst-empty"><Spinner animation="border" size="sm" /> Loading tickets...</div>
                    ) : items.length === 0 ? (
                        <div className="sst-empty">No support tickets match this filter.</div>
                    ) : (
                        <div className="sst-ticket-list">
                            {items.map((ticket) => (
                                <button key={ticket.id} type="button" className={`sst-ticket ${selected?.id === ticket.id ? 'sst-ticket-selected' : ''}`} onClick={() => setSelected(ticket)}>
                                    <div className="sst-ticket-topline">
                                        <strong>{ticket.public_id}</strong>
                                        <span className={statusClass(ticket.status)}>{formatStatus(ticket.status)}</span>
                                    </div>
                                    <span className="sst-ticket-subject">{ticket.subject}</span>
                                    <span className="sst-ticket-context">{ticket.organization_name || `Organization #${ticket.organization_id}`}{ticket.reported_by ? ` | ${ticket.reported_by.name}` : ''}</span>
                                    <span className="sst-ticket-meta">{ticket.payment ? `$${(ticket.payment.amount_cents / 100).toFixed(2)} · ` : ''}{formatDate(ticket.updated_at)}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </section>

                <section className="sst-panel sst-detail" aria-live="polite">
                    {!selected ? (
                        <div className="sst-empty sst-detail-empty"><ChatLeftText size={22} /><strong>Select a ticket</strong><span>Its charge details, conversation, and available actions will appear here.</span></div>
                    ) : (
                        <>
                            <div className="sst-panel-header sst-detail-header">
                                <div>
                                    <div className="sst-ticket-topline"><h2>{selected.public_id}</h2><span className={statusClass(selected.status)}>{formatStatus(selected.status)}</span></div>
                                    <p>Opened {formatDate(selected.created_at)}</p>
                                </div>
                            </div>

                            <div className={`sst-detail-body ${isLlmUsageRecharge(selected) ? 'sst-llm-recharge-ticket' : ''}`}>
                                <div className="sst-charge-summary">
                                    <div><span>Charge</span><strong>{selected.payment ? `$${(selected.payment.amount_cents / 100).toFixed(2)}` : 'Unavailable'}</strong></div>
                                    <div><span>Payment type</span><strong>{paymentLabel(selected)}</strong></div>
                                    <div><span>Payment status</span><strong>{selected.payment?.status?.replace(/_/g, ' ') || 'Unavailable'}</strong></div>
                                </div>

                                <div className="sst-charge-summary">
                                    <div><span>Organization</span><strong>{selected.organization_name || `Organization #${selected.organization_id}`}</strong></div>
                                    <div><span>Reported by</span><strong>{selected.reported_by ? selected.reported_by.name : 'Unavailable'}{selected.reported_by ? <small>{selected.reported_by.email}</small> : null}</strong></div>
                                    {isLlmUsageRecharge(selected) && (
                                        <div><span>LLM Usage Credits balance</span><strong>{selected.llm_usage_credit_balance_cents == null ? 'Unavailable' : `$${(selected.llm_usage_credit_balance_cents / 100).toFixed(2)}`}</strong></div>
                                    )}
                                    <div><span>Subscription plan</span><strong>{selected.subscription_plan_name || '—'}</strong></div>
                                </div>

                                <div className="sst-section">
                                    <span className="sst-section-label">Issue reported</span>
                                    <h3>{selected.reason.replace(/_/g, ' ')}</h3>
                                    <p className="sst-description">{selected.description}</p>
                                </div>

                                <div className="sst-section">
                                    <span className="sst-section-label">Conversation</span>
                                    <div className="sst-thread">
                                        {selected.messages.filter((message) => !message.is_internal).map((message) => (
                                            <div key={message.id} className="sst-message">
                                                <p>{message.body}</p>
                                                <span>{formatDate(message.created_at)}</span>
                                            </div>
                                        ))}
                                    </div>
                                    {isActionableTicket(selected) ? (
                                        <>
                                            <label className="sst-label" htmlFor="support-ticket-reply">Reply to organization admin</label>
                                            <textarea id="support-ticket-reply" className="sst-textarea" value={reply} onChange={(event) => setReply(event.target.value)} placeholder="Write a clear update for the organization admin." disabled={submitting !== null} rows={4} />
                                            <button type="button" className="sst-button sst-button-primary" onClick={handleReply} disabled={!reply.trim() || submitting !== null}>
                                                {submitting === 'reply' ? <><Spinner animation="border" size="sm" /> Sending...</> : <><ChatLeftText size={15} /> Send reply</>}
                                            </button>
                                        </>
                                    ) : (
                                        <p className="sst-readonly-notice">This ticket is closed and can no longer receive replies or billing actions.</p>
                                    )}
                                </div>

                                <div className="sst-refund-section">
                                    <div>
                                        <span className="sst-section-label">Refund action</span>
                                        <p>{remainingRefundableCents(selected) > 0 ? `Up to $${(remainingRefundableCents(selected) / 100).toFixed(2)} remains refundable. Leave the amount blank to refund it in full.` : 'This charge has already been fully refunded and cannot be refunded again.'}</p>
                                    </div>
                                    {isActionableTicket(selected) && (
                                    <div className="sst-refund-controls">
                                        <label className="visually-hidden" htmlFor="support-ticket-refund">Refund amount</label>
                                        <input id="support-ticket-refund" className="sst-input" type="number" min="0.01" step="0.01" max={(remainingRefundableCents(selected) / 100).toFixed(2)} value={refundAmount} onChange={(event) => setRefundAmount(event.target.value)} placeholder={remainingRefundableCents(selected) > 0 ? `Up to $${(remainingRefundableCents(selected) / 100).toFixed(2)}` : 'No refundable amount'} disabled={submitting !== null || remainingRefundableCents(selected) <= 0} />
                                        <button type="button" className="sst-button sst-button-secondary" onClick={handleReject} disabled={submitting !== null}>{submitting === 'reject' ? 'Rejecting...' : 'Reject ticket'}</button>
                                        <button type="button" className="sst-button sst-button-danger" onClick={handleRefund} disabled={!selected.payment || remainingRefundableCents(selected) <= 0 || submitting !== null}>{submitting === 'refund' ? <><Spinner animation="border" size="sm" /> Processing...</> : <><CashStack size={15} /> Submit refund</>}</button>
                                    </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </section>
            </div>
        </div>
    );
}
